import React, { FC, useState, useEffect } from 'react';
import { KatInput } from '@amzn/katal-react';
import styles from './TimeInput.module.css';

interface TimeInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  'data-testid': string;
}

/**
 * TimeInput component for MM:SS format time input
 * Validates format and provides user feedback
 */
export const TimeInput: FC<TimeInputProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder = 'MM:SS',
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const [timeValue, setTimeValue] = useState(value);
  const [formatError, setFormatError] = useState<string>('');

  useEffect(() => {
    setTimeValue(value);
    // Validate initial/updated value
    if (value && !validateTimeFormat(value)) {
      setFormatError('Invalid format. Use MM:SS (e.g., 01:30)');
    } else {
      setFormatError('');
    }
  }, [value]);

  const validateTimeFormat = (timeStr: string): boolean => {
    if (!timeStr) return true; // Empty is valid
    const timeRegex = /^([0-5][0-9]):([0-5][0-9])$/; // Require zero-padded format
    return timeRegex.test(timeStr);
  };

  const handleChange = (event: KatInput.ChangeEvent) => {
    const newValue = event.currentTarget.value;
    setTimeValue(newValue);

    if (!newValue) {
      setFormatError('');
      onChange('');
      return;
    }

    if (validateTimeFormat(newValue)) {
      setFormatError('');
      onChange(newValue);
    } else {
      setFormatError('Invalid format. Use MM:SS (e.g., 01:30)');
    }
  };

  const displayError = error || formatError;

  return (
    <div className={styles.container}>
      <KatInput
        label={label}
        value={timeValue}
        onInput={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={dataTestId}
      />
      {displayError && (
        <div className={styles.errorText} data-testid={`${dataTestId}-error`}>
          {displayError}
        </div>
      )}
    </div>
  );
};
